/**
 * Subscription levels (web-pose-extraction step 3).
 *
 * Replaces the ad-hoc "email starts with olly.dolan" checks scattered through the frontend with
 * one resolver + per-level policies. PREMIUM exists for the coming tiering work; today auth0 gives
 * us only the email, so ADMIN = the internal-account prefix and everyone else is FREE.
 */
export enum SubscriptionLevel {
  FREE = "FREE",
  PREMIUM = "PREMIUM",
  ADMIN = "ADMIN",
}

const ADMIN_EMAIL_PREFIX = "olly.dolan";

export function subscriptionLevelForEmail(email: string | null | undefined): SubscriptionLevel {
  if (email && email.toLowerCase().startsWith(ADMIN_EMAIL_PREFIX)) return SubscriptionLevel.ADMIN;
  return SubscriptionLevel.FREE;
}

/** Upload duration cap — unchanged semantics from the old inline check (public 20s, internal 90s). */
export function maxUploadDurationSec(level: SubscriptionLevel): number {
  return level === SubscriptionLevel.ADMIN ? 90 : 20;
}

/** "Extract pose on this device" defaults ON for everyone except ADMIN (internal accounts keep
 *  exercising the full GPU path by default). */
export function defaultsToClientExtraction(level: SubscriptionLevel): boolean {
  return level !== SubscriptionLevel.ADMIN;
}

/** Only ADMIN may opt OUT of on-device extraction. Basic users are front-end labels only — the
 *  toggle is not shown and extraction always runs; the server stamps their uploads gpuBypass. */
export function canToggleClientExtraction(level: SubscriptionLevel): boolean {
  return level === SubscriptionLevel.ADMIN;
}

/** What the upload asks the server for. Client-side pose extraction runs for EVERY upload;
 *  "free" = analysis on those labels (positions, stills, scorecard), "premium" = the in-depth
 *  run (GPU labels + narrated video). Mirrors SubscriptionPolicy.GpuBypassFor server-side,
 *  which also gates the claim — the UI choice is convenience, not authority. */
export type AnalysisMode = "free" | "premium";

export function canSelectPremium(level: SubscriptionLevel): boolean {
  return level === SubscriptionLevel.PREMIUM || level === SubscriptionLevel.ADMIN;
}

export function defaultAnalysisMode(level: SubscriptionLevel): AnalysisMode {
  return canSelectPremium(level) ? "premium" : "free";
}
